import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LogOut, FileText, MessageSquare, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';

import { AdminLogin } from './admin/AdminLogin';
import { PostEditor } from './admin/PostEditor';
import { DeleteConfirm } from './admin/DeleteConfirm';
import { StatsBar } from './admin/StatsBar';
import { PostsManager } from './admin/PostsManager';
import { ContactsManager } from './admin/ContactsManager';
import { API, getAuthToken, getAuthHeaders } from './admin/authHelpers';
import { logError } from '../lib/logger';

export function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');

  const handleLogout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [postsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/blog?limit=100`),
        axios.get(`${API}/contact`, { headers })
      ]);
      setPosts(postsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      logError('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const verifyToken = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const response = await axios.get(`${API}/admin/verify`, {
            headers: getAuthHeaders()
          });
          if (response.data.valid) {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('admin_token');
          }
        } catch (error) {
          localStorage.removeItem('admin_token');
        }
      }
      setInitializing(false);
    };
    verifyToken();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleDeletePost = useCallback(async () => {
    if (!deletingPost) return;

    try {
      await axios.delete(`${API}/blog/${deletingPost.slug}`, {
        headers: getAuthHeaders()
      });
      setPosts(prev => prev.filter(p => p.id !== deletingPost.id));
      setDeletingPost(null);
    } catch (error) {
      logError('Error deleting post:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        handleLogout();
      } else {
        alert('Error deleting post');
      }
    }
  }, [deletingPost, handleLogout]);

  const handleSavePost = useCallback(() => {
    setShowEditor(false);
    setEditingPost(null);
    fetchData();
  }, [fetchData]);

  const stats = {
    totalPosts: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    drafts: posts.filter(p => p.status === 'draft').length,
    totalViews: posts.reduce((sum, p) => sum + (p.views || 0), 0),
    contacts: contacts.length,
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-neon-purple animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-void" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-void/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="ServeNetics Logo"
                className="h-8 w-auto"
              />
              <span className="font-heading font-semibold text-white">Admin</span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-white/60 hover:text-white text-sm transition-colors"
            >
              View Site
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <StatsBar stats={stats} />

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'posts'
                ? 'bg-neon-purple text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="tab-posts"
          >
            <FileText className="w-4 h-4" />
            Blog Posts
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'contacts'
                ? 'bg-neon-purple text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="tab-contacts"
          >
            <MessageSquare className="w-4 h-4" />
            Contact Submissions
          </button>
        </div>

        {activeTab === 'posts' && (
          <PostsManager
            posts={posts}
            loading={loading}
            onCreate={() => { setEditingPost(null); setShowEditor(true); }}
            onEdit={(post) => { setEditingPost(post); setShowEditor(true); }}
            onDelete={(post) => setDeletingPost(post)}
          />
        )}

        {activeTab === 'contacts' && (
          <ContactsManager contacts={contacts} />
        )}
      </div>

      {/* Post Editor Modal */}
      <AnimatePresence>
        {showEditor && (
          <PostEditor
            post={editingPost}
            onSave={handleSavePost}
            onClose={() => { setShowEditor(false); setEditingPost(null); }}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deletingPost && (
          <DeleteConfirm
            post={deletingPost}
            onConfirm={handleDeletePost}
            onCancel={() => setDeletingPost(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminDashboard;
